import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { COMPANY, NAV_LINKS } from '../mock';

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <>
      {/* Top announcement bar */}
      <div className="hidden md:flex items-center justify-center gap-4 bg-[#0A1628] text-white text-xs py-2 px-4">
        <div className="flex items-center gap-1.5">
          <Shield className="w-3.5 h-3.5 text-[#2AA8E8]" />
          <span className="font-semibold text-white/90">{COMPANY.mc}</span>
          <span className="text-white/40">·</span>
          <span className="font-semibold text-white/90">{COMPANY.dot}</span>
        </div>
        <span className="text-white/30">|</span>
        <span className="opacity-90">⚡ Get an instant quote and unlock up to 20% savings today.</span>
        <Link to="/#quote" className="underline underline-offset-4 hover:text-[#D4A24C] transition-colors">Get Quote</Link>
      </div>

      <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white'}`}>
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 xl:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group shrink-0">
            <div className="relative w-11 h-11 md:w-12 md:h-12 rounded-xl overflow-hidden bg-[#0A1628] flex items-center justify-center border border-[#0E2744] group-hover:border-[#2AA8E8] transition-colors shrink-0">
              <img src={COMPANY.logo} alt="Excelence Inc" className="w-full h-full object-cover scale-150" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-extrabold text-lg md:text-xl tracking-tight whitespace-nowrap">
                <span className="text-[#2AA8E8]">EXCELENCE</span>{' '}
                <span className="gold-text">INC.</span>
              </span>
              <span className="hidden md:block text-[10px] uppercase tracking-[0.2em] text-[#4A5A72] font-semibold whitespace-nowrap">
                {COMPANY.mc} · {COMPANY.dot}
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
            {NAV_LINKS.map(l => (
              <a key={l.label} href={l.href} className="whitespace-nowrap text-sm font-semibold text-[#0A1628]/80 hover:text-[#0A1628] transition-colors relative group">
                {l.label}
                <span className="absolute left-0 -bottom-1 w-0 h-[2px] bg-[#2AA8E8] group-hover:w-full transition-all"></span>
              </a>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2 lg:gap-3 shrink-0">
            <a href={COMPANY.phoneHref} className="hidden 2xl:flex items-center gap-2 text-sm font-bold text-[#0A1628] hover:text-[#2AA8E8] transition-colors whitespace-nowrap">
              <Phone className="w-4 h-4" />
              {COMPANY.phone}
            </a>
            <Link
              to="/admin"
              data-testid="header-staff-login-link"
              className="hidden md:inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#0A1628]/70 hover:text-[#2AA8E8] transition-colors border border-[#E5EAF2] hover:border-[#2AA8E8] rounded-full px-3 py-1.5 whitespace-nowrap"
            >
              <Shield className="w-3.5 h-3.5" />
              Staff Login
            </Link>
            <a href="/#quote">
              <Button className="btn-accent font-bold hidden sm:inline-flex whitespace-nowrap">Get a Quote</Button>
            </a>
            <button onClick={() => setOpen(!open)} className="lg:hidden p-2 rounded-md hover:bg-[#F7F9FC]" aria-label="Menu">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t border-[#E5EAF2] bg-white">
            <div className="px-4 py-4 flex flex-col gap-3">
              {NAV_LINKS.map(l => (
                <a key={l.label} href={l.href} className="py-2 text-[#0A1628] font-semibold border-b border-[#F0F3F8]">{l.label}</a>
              ))}
              <a href={COMPANY.phoneHref} className="py-2 text-[#0A1628] font-bold flex items-center gap-2">
                <Phone className="w-4 h-4" /> {COMPANY.phone}
              </a>
              <Link to="/admin" data-testid="header-staff-login-link-mobile" className="py-2 text-[#0A1628] font-bold flex items-center gap-2">
                <Shield className="w-4 h-4" /> Staff Login
              </Link>
              <div className="text-xs text-[#4A5A72] font-semibold">{COMPANY.mc} · {COMPANY.dot}</div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
